import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Button, Card, DataTable, EmptyState, FilterBar, FilterChips, FiltroPill,
  Pagination, Pill, type Column, fmtBRL,
} from "@atlas/ui/web";
import { aplicarFiltros, contagens, motivoVazio, POR_PAGINA_PADRAO, recortar } from "../../lib/recorte";
import { matchAnyKeys } from "../../lib/text-search";
import { useFiltros } from "../../lib/use-filtros";
import { atlas } from "../../lib/sdk";
import { useUnidadeAtiva } from "../../lib/unidade-ativa";
import type { PrefeituraAdf } from "@atlas/sdk";
import { PageHeader, downloadAuthed } from "./_ui";
import { HistoricoPagamentosContratoModal } from "../../components/HistoricoPagamentosContratoModal";
import { AutorizacoesDoServidor } from "./autorizacoes-servidor";


// Prefeitura APENAS RECEBE/CONSULTA as ADFs. Cliente disse: "a averbadora que
// faz a adf, a prefeitura so recebe". Botoes de Confirmar / Reportar falha
// foram movidos para a averbadora (/averbadora/adf).

/**
 * O status do lote como ESTA TELA o conta — `parcial` fechado como `aplicada`.
 *
 * Decisao do cliente em 15/09/2026. A pergunta que a tela de ADF responde e'
 * "a folha processou o lote do mes?", e para essa pergunta descontar parte e'
 * uma ADF resolvida: a folha respondeu, o dinheiro saiu. QUANTO saiu e' outra
 * pergunta, de outro objeto — o contrato —, e ela se responde em
 * /prefeitura/contratos, que marca a linha e abre o historico por competencia.
 *
 * Passa pelo filtro, pelas contagens dos chips E pelo pill de proposito: as
 * tres tem de concordar. Normalizar so' no pill deixaria a linha escrita
 * "aplicada" fora do chip "Aplicadas" — e o operador conferindo na mao acharia
 * erro de dado onde ha dois vocabularios na mesma tela.
 */
function statusNaTela(status: PrefeituraAdf["status"]): string {
  return status === "parcial" ? "aplicada" : status;
}

export function PrefeituraAdf() {
  // A organizacao sobre a qual esta tela opera — a do login, ou a atendida
  // escolhida no seletor do cabecalho.
  const { chaveDaUnidade, opcoesDoSdk } = useUnidadeAtiva();
  const comps = useQuery({
    queryKey: ["prefeitura", "adf-comps", ...chaveDaUnidade()],
    queryFn: () => atlas.prefeitura.adfCompetencias(opcoesDoSdk()),
  });
  const [competencia, setCompetencia] = useState<string>("");
  useEffect(() => { if (!competencia && comps.data?.competenciaAtual) setCompetencia(comps.data.competenciaAtual); }, [comps.data, competencia]);

  const adfs = useQuery({
    queryKey: ["prefeitura", "adf", competencia, ...chaveDaUnidade()],
    queryFn: () => atlas.prefeitura.adf(competencia, opcoesDoSdk()),
    enabled: !!competencia,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });
  // Recentes no topo — igual /averbadora/adf. Toda troca de status bumpa
  // `atualizadoEm` no backend, entao a linha sobe pro topo automaticamente.
  const rows = useMemo(() => {
    const base = adfs.data?.adfs ?? [];
    return [...base].sort((a, b) => {
      const ta = a.atualizadoEm ?? "";
      const tb = b.atualizadoEm ?? "";
      if (ta && tb) return tb.localeCompare(ta);
      if (ta) return -1;
      if (tb) return 1;
      return 0;
    });
  }, [adfs.data]);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const [statusFiltro, setStatusFiltro] = useState("");
  const [banco, setBanco] = useState("");
  const [busca, setBusca] = useState("");
  const [detalhes, setDetalhes] = useState<PrefeituraAdf | null>(null);

  // FILTROS NOMEADOS, para as contagens poderem ignorar a propria dimensao.
  //
  // Sem nome nao existe `exceto` (FR-010): a contagem de um chip tem de dizer
  // "quantos eu veria se clicasse aqui", e para isso sai da base com todos os
  // OUTROS filtros aplicados menos o dela. Antes so' havia status, e contar
  // sobre `rows` bastava; com busca e banco no meio, contar sobre `rows` faria
  // o chip prometer linhas que a busca ja excluiu.
  //
  // Busca SEM debounce, de proposito: o recorte e' no cliente e instantaneo, e
  // quem procura um ADF que nao lembra direito se beneficia do retorno imediato.
  // Debounce serve quando cada tecla e' um request — nao e' o caso aqui.
  const termo = busca.trim();
  const filtros = useMemo(() => ({
    status: statusFiltro ? (a: PrefeituraAdf) => statusNaTela(a.status) === statusFiltro : null,
    banco: banco ? (a: PrefeituraAdf) => a.bancoNome === banco : null,
    busca: termo
      ? (a: PrefeituraAdf) => matchAnyKeys(a, termo, ["adf", "idUnico", "cpfMasked", "nome", "matricula"])
      : null,
  }), [statusFiltro, banco, termo]);

  const filtradas = useMemo(() => aplicarFiltros(rows, filtros), [rows, filtros]);
  const contagemStatus = useMemo(() => contagens(aplicarFiltros(rows, filtros, "status"), (a) => statusNaTela(a.status)), [rows, filtros]);
  const semStatus = useMemo(() => aplicarFiltros(rows, filtros, "status"), [rows, filtros]);
  const opcoesBanco = useMemo(() => {
    const c = contagens(aplicarFiltros(rows, filtros, "banco"), (a) => a.bancoNome);
    return Object.entries(c)
      .filter(([nome]) => nome)
      .sort(([a], [b]) => a.localeCompare(b, "pt-BR"))
      .map(([nome, n]) => ({ value: nome, label: `${nome} (${n})` }));
  }, [rows, filtros]);

  const filtrosUi = useFiltros(
    [
      // Competencia e' CONTEXTO, nao filtro: sem ela a consulta nem roda
      // (`enabled: !!competencia`). Nao conta no contador e o reset nao mexe —
      // limpar a competencia esvaziaria a tela em vez de ampliar o recorte.
      { nome: "competencia", ligado: true, contexto: true, limpar: () => {} },
      { nome: "busca", ligado: busca !== "", limpar: () => setBusca("") },
      { nome: "status", ligado: statusFiltro !== "", limpar: () => setStatusFiltro("") },
      { nome: "banco", ligado: banco !== "", limpar: () => setBanco("") },
    ],
    setPagina,
  );
  const { filtrar, temFiltro } = filtrosUi;

  // Trocar de competencia com a posicao antiga cairia num offset que ja nao
  // existe. As outras dimensoes passam pelo `filtrar()`, que ja zera a pagina.
  useEffect(() => { setPagina(1); }, [competencia]);

  const daPagina = recortar(filtradas, pagina, porPagina);

  // TOTAL DO RECORTE, e isto e' uma correcao.
  //
  // Antes somava `rows` — a competencia inteira — com um comentario dizendo que
  // era "o recorte, nunca a pagina". As duas coisas nao sao a mesma: com o
  // filtro de status ligado, `rows` ja era maior que o recorte, e o rodape
  // anunciava um total que a tabela nao mostrava. Com busca e banco no meio a
  // divergencia ficaria grande o suficiente para alguem conferir na mao e achar
  // erro de dado onde ha erro de denominador (FR-014).
  const totalParcelas = useMemo(() => filtradas.reduce((s, a) => s + a.valorParcela, 0), [filtradas]);

  const columns: Column<PrefeituraAdf>[] = [
    { key: "adf", header: "ADF", mono: true },
    { key: "idUnico", header: "ID único", mono: true },
    { key: "cpfMasked", header: "CPF", mono: true },
    { key: "nome", header: "Servidor" },
    { key: "bancoNome", header: "Consignatária" },
    {
      key: "valorParcela",
      header: "Parcela",
      align: "right",
      render: (a) => (
        <span>
          {fmtBRL(a.valorParcela)}
          <span style={{ color: "var(--text-muted)", marginLeft: 6, fontSize: 12 }}>
            × {a.totalParcelas}x
          </span>
        </span>
      ),
    },
    { key: "status", header: "Status", render: (a) => {
      // `parcial` chega aqui ja fechado como `aplicada` (ver statusNaTela).
      const s = statusNaTela(a.status);
      return (
        <Pill variant={
          s === "aplicada" ? "averbado"
          : s === "falha" || s === "interrompida_desligamento" ? "expirado"
          : "pendente"
        }>{s}</Pill>
      );
    } },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <PageHeader
        title="ADF — Descontos em folha"
        subtitle="Lote mensal de ADFs recebidas. A averbadora é quem aplica em folha e reporta falhas — esta tela é para consulta e download."
      />

      <div style={{
        padding: "10px 14px",
        borderRadius: 10,
        background: "color-mix(in srgb, var(--accent) 6%, transparent)",
        border: "1px solid color-mix(in srgb, var(--accent) 30%, transparent)",
        fontSize: 12.5,
        color: "var(--text-muted)",
        lineHeight: 1.5,
      }}>
        <b style={{ color: "var(--text)" }}>ℹ️ Só recebimento:</b> a averbadora executa a aplicação em folha e reporta falhas. Esta prefeitura consulta o lote e baixa CSV/PDF para conferência interna. Qualquer alteração de status vem da averbadora e reflete aqui automaticamente.
      </div>

      <Card>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>Competência:</span>
          {(comps.data?.competencias ?? []).map((c) => (
            <button
              key={c.competencia}
              onClick={() => setCompetencia(c.competencia)}
              style={{
                padding: "6px 12px", borderRadius: 999, cursor: "pointer", fontSize: 13,
                border: `1px solid ${competencia === c.competencia ? "var(--accent)" : "var(--border)"}`,
                background: competencia === c.competencia ? "color-mix(in srgb, var(--accent) 14%, transparent)" : "var(--surface)",
                color: "var(--text)",
              }}
            >
              {c.competencia} <span style={{ color: "var(--text-muted)" }}>({c.aplicadas}/{c.total})</span>
            </button>
          ))}
          {(comps.data?.competencias.length ?? 0) === 0 ? <span style={{ fontSize: 13 }}>{comps.data?.competenciaAtual ?? "—"}</span> : null}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
          {competencia ? <>
            <Button size="sm" variant="ghost" onClick={() => downloadAuthed(atlas.prefeitura.adfCsvUrl(competencia, opcoesDoSdk()), `adf-${competencia}.csv`)}>Baixar CSV</Button>
            <Button size="sm" variant="ghost" onClick={() => downloadAuthed(atlas.prefeitura.adfPdfUrl(competencia, opcoesDoSdk()), `adf-lote-${competencia}.pdf`)}>Baixar PDF</Button>
          </> : null}
          <span style={{ flex: 1 }} />
          {/* OS DOIS DENOMINADORES, DECLARADOS.
              Antes esta linha dizia "{rows.length} ADFs" — a competencia
              inteira — ao lado de uma tabela que podia estar recortada por
              status. Dois numeros diferentes sobre a mesma tela, sem dizer de
              que conjunto cada um falava; quem confere na mao acha erro de dado
              onde ha erro de denominador.
              A quebra por recebidas/aplicadas/falhas saiu daqui: os chips de
              Status abaixo carregam exatamente essas contagens e, com o
              `exceto`, elas respeitam busca e banco. Manter as duas seria duas
              fontes para o mesmo numero — e elas DIVERGIRIAM assim que a busca
              fosse usada, porque a daqui nao respeitava os outros filtros. */}
          <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
            {temFiltro
              ? <><b style={{ color: "var(--text)" }}>{filtradas.length}</b> no recorte · {rows.length} na competência</>
              : <>{rows.length} ADFs</>}
            {" · total parcelas "}
            <b style={{ color: "var(--text)" }}>{fmtBRL(totalParcelas)}</b>
            {temFiltro ? <span style={{ fontSize: 12 }}> (do recorte)</span> : null}
          </span>
        </div>
      </Card>

      {/* Status e competencia ficam VISIVEIS — sao a fila de trabalho e o
          contexto, e esconde-los atras de um botao faria a tela recortar sem
          dizer que recorta. Busca e banco entram na mesma barra: o operador que
          recebe um chamado tem um ADF, um ID unico ou um CPF na mao, e ate aqui
          a unica forma de achar a linha era percorrer as paginas. */}
      <FilterBar
        layout="linha"
        searchValue={busca}
        onSearchChange={filtrar(setBusca)}
        searchPlaceholder="Buscar por ADF, ID único, CPF, nome ou matrícula"
        {...filtrosUi.propsFilterBar}
        actions={
          <FilterChips
            label="Status"
            value={statusFiltro}
            onChange={filtrar(setStatusFiltro)}
            options={[
              // "Todas" conta a base SEM o filtro de status, mas COM busca e
              // banco — e' o que o operador veria ao desligar so' o status.
              { value: "", label: "Todas", count: semStatus.length },
              { value: "recebida", label: "Recebidas", cor: "var(--gold-500)", count: contagemStatus.recebida ?? 0 },
              // Conta as parciais junto, pela mesma regra do pill (statusNaTela).
              { value: "aplicada", label: "Aplicadas", cor: "var(--emerald-500)", count: contagemStatus.aplicada ?? 0 },
              { value: "falha", label: "Falhas", cor: "var(--danger-500)", count: contagemStatus.falha ?? 0 },
            ]}
          />
        }
      >
        <FiltroPill
          label="Consignatária"
          opcoes={opcoesBanco}
          // Uma prefeitura conferindo o lote pergunta "as ADFs do banco X", nao
          // "compare X com Y" — selecao unica resolve, e a pill e' o controle
          // certo pela cardinalidade (um municipio grande tem dezenas de
          // conveniados) e pela busca que ela ganha acima de 8 opcoes.
          selecionados={banco ? [banco] : []}
          onChange={(vs: string[]) => filtrar(setBanco)(vs[vs.length - 1] ?? "")}
          buscaPlaceholder="Buscar consignatária"
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(a) => a.id}
        loading={adfs.isLoading}
        emptyState={
          adfs.isError
            ? "Não foi possível carregar as ADFs desta competência. Tente novamente."
            : <EmptyState
                // Recorte no cliente, entao a base e' a competencia inteira e a
                // tela tem os dois numeros sem precisar da rota.
                motivo={!competencia ? "pre-condicao" : motivoVazio(rows.length, filtradas.length)}
                preCondicao="Escolha uma competência para ver as ADFs recebidas."
                filtroVazio="Nenhuma ADF bate com esses filtros nesta competência."
                semDados="Nenhuma ADF recebida nesta competência."
                onLimpar={filtrosUi.limparFiltros}
              />
        }
      />
      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="ADFs"
      />
      {detalhes ? (
        <HistoricoPagamentosContratoModal
          adf={detalhes.adf}
          carregar={() => atlas.prefeitura.historicoPagamentosAdf(detalhes.adf, opcoesDoSdk())}
          competenciaDestaque={competencia}
          operacional
          onClose={() => setDetalhes(null)}
        />
      ) : null}
      {/* #282: inclusoes e aumentos que dependem da autorizacao do servidor. */}
      <AutorizacoesDoServidor competencia={competencia} />
    </div>
  );
}
